import type { CommandCenterState } from '../state/commandCenterStore';
import type { AgentCard, SectionSeed } from '../types';
import { cardFromCatalogAgent, UNREGISTERED_BADGE, type RunStatsMap } from './catalog';

/** The board-only section that collects Hermes Skills which ran but have no card. */
export const UNREGISTERED_SECTION = 'unregistered';

const HERMES_CARD_PREFIX = 'hermes-';

const UNREGISTERED_SECTION_SEED: SectionSeed = {
  slug: UNREGISTERED_SECTION,
  title: '미등록',
  desc: 'Hermes Skills that ran this week but have no card on the board.',
  owner: '—',
  prefix: '',
};

export const isUnregisteredCard = (card: AgentCard): boolean => card.section === UNREGISTERED_SECTION;

/** A card built from a run's agent id alone; the id is `hermes-<skill>`, so the name is the Skill. */
const unregisteredCard = (agentId: string): AgentCard => ({
  ...cardFromCatalogAgent({
    id: agentId,
    code: '',
    sectionId: UNREGISTERED_SECTION,
    name: agentId.slice(HERMES_CARD_PREFIX.length),
    objective: null,
    description: 'Ran on a Hermes node; not registered on the board.',
    owner: null,
    runtime: 'hermes',
    lifecycle: 'live',
    progress: null,
    targetDate: null,
    note: null,
    sortOrder: 0,
  }),
  badge: UNREGISTERED_BADGE,
});

/** The board plus a section of Hermes Skills that ran with no card, busiest first, so no run goes uncounted. */
export const withUnregisteredCards = (state: CommandCenterState, stats: RunStatsMap): CommandCenterState => {
  const known = new Set([...state.catalogCards.map((card) => card.id), ...Object.keys(state.planned)]);
  const ids = Object.entries(stats)
    .filter(([id, counts]) => id.startsWith(HERMES_CARD_PREFIX) && !known.has(id) && counts.runs > 0)
    .sort(([, a], [, b]) => b.runs - a.runs)
    .map(([id]) => id);
  if (ids.length === 0) {
    return state.tab === UNREGISTERED_SECTION ? { ...state, tab: 'all' } : state;
  }
  return {
    ...state,
    sections: [...state.sections, UNREGISTERED_SECTION_SEED],
    catalogCards: [...state.catalogCards, ...ids.map(unregisteredCard)],
    layout: { ...state.layout, [UNREGISTERED_SECTION]: ids },
  };
};
